import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const LoginForm = () => {
  const { login } = useAuth();
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };
  

const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      await login(formData.email, formData.password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Giriş yapılırken bir hata oluştu.');
      console.error('Login error:', err); // Debugging için
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <FormContainer onSubmit={handleSubmit}>
      {error && <ErrorMessage>{error}</ErrorMessage>}
      
      {/* Email Input */}
      <InputGroup>
        <InputLabel $isActive={focusedField === 'email' || formData.email.length > 0}>
          Email
        </InputLabel>
        <InputWrapper>
          <InputIcon>📧</InputIcon>
          <StyledInput
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            onFocus={() => setFocusedField('email')}
            onBlur={() => setFocusedField(null)}
            required
            disabled={isLoading}
          />
          <InputLine $isActive={focusedField === 'email'} />
        </InputWrapper>
      </InputGroup>

      {/* Şifre Input */}
      <InputGroup>
        <InputLabel $isActive={focusedField === 'password' || formData.password.length > 0}>
          Şifre
        </InputLabel>
        <InputWrapper>
          <InputIcon>🔒</InputIcon>
          <StyledInput
            type="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            onFocus={() => setFocusedField('password')}
            onBlur={() => setFocusedField(null)}
            required
            disabled={isLoading}
          />
          <InputLine $isActive={focusedField === 'password'} />
        </InputWrapper>
      </InputGroup>

      {/* Ek Özellikler */}
      <FormExtras>
        <RememberMe>
          <Checkbox type="checkbox" id="remember" disabled={isLoading} />
          <CheckboxLabel htmlFor="remember">Beni Hatırla</CheckboxLabel>
        </RememberMe>
        <ForgotPassword>Şifremi Unuttum</ForgotPassword>
      </FormExtras>

      {/* Giriş Butonu */}
      <SubmitButton type="submit" disabled={isLoading}>
        <ButtonContent>
          <ButtonIcon>{isLoading ? '⌛' : '⚡'}</ButtonIcon>
          <span>{isLoading ? 'Giriş Yapılıyor...' : 'Giriş Yap'}</span>
        </ButtonContent>
        <ButtonGlow />
      </SubmitButton>
    </FormContainer>
  );
};

// Animasyonlar
const slideUp = keyframes`
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

// Styled Components
const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
  animation: ${slideUp} 0.5s ease forwards;
`;

const ErrorMessage = styled.div`
  color: #ff4d4f;
  background: rgba(255, 77, 79, 0.1);
  border: 1px solid rgba(255, 77, 79, 0.2);
  padding: 10px;
  border-radius: 8px;
  margin-bottom: 15px;
  font-size: 0.9rem;
`;

const InputGroup = styled.div`
  position: relative;
  margin-bottom: 10px;
`;

const InputLabel = styled.label<{ $isActive: boolean }>`
  position: absolute;
  left: 45px;
  color: ${props => props.$isActive ? '#40A9FF' : '#8B9CC8'};
  font-size: ${props => props.$isActive ? '0.8rem' : '1rem'};
  top: ${props => props.$isActive ? '-20px' : '50%'};
  transform: translateY(-50%);
  transition: all 0.3s ease;
  pointer-events: none;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const InputIcon = styled.span`
  position: absolute;
  left: 15px;
  font-size: 1.2rem;
  color: #8B9CC8;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 15px 15px 15px 45px;
  background: rgba(64, 169, 255, 0.05);
  border: 1px solid transparent;
  border-radius: 10px;
  color: #fff;
  font-size: 1rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    background: rgba(64, 169, 255, 0.1);
    border-color: #40A9FF;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const InputLine = styled.div<{ $isActive: boolean }>`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 2px;
  width: 100%;
  background: linear-gradient(90deg, #40A9FF, #9D72FF);
  transform: scaleX(${props => props.$isActive ? 1 : 0});
  transition: transform 0.3s ease;
`;

const FormExtras = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: -10px;
`;

const RememberMe = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Checkbox = styled.input`
  accent-color: #40A9FF;
  width: 16px;
  height: 16px;

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const CheckboxLabel = styled.label`
  color: #8B9CC8;
  font-size: 0.9rem;
  cursor: pointer;
`;

const ForgotPassword = styled.a`
  color: #40A9FF;
  font-size: 0.9rem;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const SubmitButton = styled.button`
  position: relative;
  width: 100%;
  padding: 15px;
  background: linear-gradient(135deg, #40A9FF 0%, #9D72FF 100%);
  border: none;
  border-radius: 10px;
  color: white;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  overflow: hidden;
  transition: transform 0.3s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const ButtonContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  position: relative;
  z-index: 1;
`;

const ButtonIcon = styled.span`
  font-size: 1.2rem;
`;

const ButtonGlow = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #40A9FF 0%, #9D72FF 100%);
  filter: blur(15px);
  opacity: 0;
  transition: opacity 0.3s ease;

  ${SubmitButton}:hover:not(:disabled) & {
    opacity: 0.5;
  }
`;

export default LoginForm;